import React, { useState, useEffect } from 'react';
import ExportButtons from './ExportButtons';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement, PointElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend, ArcElement, BarElement, Filler
} from 'chart.js';
import * as Authservices from '../Services/services';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ArcElement, BarElement, Filler);

const tooltipStyle = {
  backgroundColor: 'rgba(18,18,30,0.95)', borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1, padding: 12, cornerRadius: 8, titleColor: '#fff', bodyColor: 'rgba(255,255,255,0.8)',
};
const gridStyle = { color: 'rgba(255,255,255,0.04)' };
const tickStyle = { color: 'rgba(255,255,255,0.4)', font: { size: 11 } };
const noGrid = { display: false };
const noBorder = { display: false };

const PlayerAnalytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Authservices.getPlayerAnalytics(timeRange)
      .then(res => {
        if (res?.status === 200) {
          setApiData(res.data?.responseDetails || null);
          console.log('[PlayerAnalytics] API data loaded:', res.data?.responseDetails);
        }
      })
      .catch(err => {
        console.warn('[PlayerAnalytics] API error, using defaults:', err?.response?.data?.responseMessage || err.message);
      })
      .finally(() => setLoading(false));
  }, [timeRange]);

  // Map actual API fields to the shape expected by sub-components
  const computedKpi = apiData ? {
    first_frame_load_p95_s:  apiData.avg_startup_time_ms != null ? (apiData.avg_startup_time_ms / 1000).toFixed(2) : null,
    crash_free_rate_pct:     apiData.error_rate_pct       != null ? (100 - apiData.error_rate_pct).toFixed(1)      : null,
    avg_rebuffer_ratio_pct:  apiData.rebuffer_ratio_pct   != null ? apiData.rebuffer_ratio_pct                     : null,
    offline_downloads_per_day: null,
    completion_rate_pct:     apiData.completion_rate_pct  != null ? apiData.completion_rate_pct                    : null,
    avg_episodes_per_session:apiData.avg_episodes_per_session != null ? apiData.avg_episodes_per_session           : null,
    avg_session_duration_sec:apiData.avg_session_duration_sec != null ? apiData.avg_session_duration_sec           : null,
  } : null;

  const exportData = [
    { metric: 'Avg Startup Time',    value: computedKpi?.first_frame_load_p95_s  ? `${computedKpi.first_frame_load_p95_s}s`   : '1.2s',   platform: 'All' },
    { metric: 'Crash-Free Rate',     value: computedKpi?.crash_free_rate_pct     ? `${computedKpi.crash_free_rate_pct}%`     : '99.6%', platform: 'All' },
    { metric: 'Avg Rebuffer Ratio',  value: computedKpi?.avg_rebuffer_ratio_pct  ? `${computedKpi.avg_rebuffer_ratio_pct}%`  : '0.8%',  platform: 'All' },
    { metric: 'Completion Rate',     value: computedKpi?.completion_rate_pct     ? `${computedKpi.completion_rate_pct}%`     : '68%',   platform: 'All' },
  ];

  return (
    <div className="pa-container">
      <div className="pa-header">
        <div className="pa-header-left">
          <h4>Player Analytics {loading && <span style={{ fontSize: '12px', color: '#8a8a9a', marginLeft: 8 }}>Loading…</span>}</h4>
          <p className="pa-subtitle">Playback performance, quality metrics & device analytics</p>
        </div>
        <div className="pa-header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ExportButtons data={exportData} headers={[{ key: 'metric', label: 'Metric' }, { key: 'value', label: 'Value' }, { key: 'platform', label: 'Platform' }]} title="Player Analytics Report" filename="player_analytics" />
          <div className="pa-time-range">
            {['7d', '14d', '30d', '90d'].map(r => (
              <button key={r} className={`pa-range-btn ${timeRange === r ? 'active' : ''}`} onClick={() => setTimeRange(r)}>{r}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="pa-section">
        <KPICards data={computedKpi} />
        <FirstFrameLoadChart data={apiData?.first_frame_load} timeRange={timeRange} />
        <div className="pa-row-2col">
          <RebufferRateChart data={apiData?.quality_distribution} />
          <ABRSwitchingChart data={apiData?.abr_switching_24h} />
        </div>
        <PlatformBreakdown data={apiData?.platform_breakdown} />
        <PlayerSDKVersionsTable data={apiData?.sdk_versions} />
        <PlaybackErrorBreakdown data={apiData?.playback_errors} />
        <DevicePerformanceTable data={apiData?.device_performance} />
      </div>
    </div>
  );
};

const KPICards = ({ data }) => {
  const metrics = [
    { label: 'First Frame Load', value: data ? `P95: ${data.first_frame_load_p95_s}s` : 'P95: 1.2s', change: data?.first_frame_load_change || '-0.1s', positive: true, icon: 'fas fa-play-circle', color: '#f82581' },
    { label: 'Crash-Free Rate', value: data ? `${data.crash_free_rate_pct}%` : '99.6%', change: data?.crash_free_change || '+0.2%', positive: true, icon: 'fas fa-shield-alt', color: '#10b981' },
    { label: 'Avg Rebuffer Ratio', value: data ? `${data.avg_rebuffer_ratio_pct}%` : '0.8%', change: data?.avg_rebuffer_change || '-0.15%', positive: true, icon: 'fas fa-sync-alt', color: '#f59e0b' },
    { label: 'Offline Downloads', value: data ? `${(data.offline_downloads_per_day / 1000).toFixed(1)}K/day` : '24.5K/day', change: data?.offline_downloads_change || '+3.2K', positive: true, icon: 'fas fa-cloud-download-alt', color: '#6366f1' },
  ];
  return (
    <div className="pa-metrics-grid">
      {metrics.map((m, i) => (
        <div key={i} className="pa-metric-card" style={{ animationDelay: `${i * 0.06}s` }}>
          <div className="pa-metric-top">
            <div className="pa-metric-icon" style={{ background: m.color + '15', color: m.color }}><i className={m.icon}></i></div>
            <span className={`pa-metric-change ${m.positive ? 'up' : 'down'}`}><i className={`fas fa-arrow-${m.positive ? 'up' : 'down'}`}></i> {m.change}</span>
          </div>
          <div className="pa-metric-value">{m.value}</div>
          <div className="pa-metric-label">{m.label}</div>
        </div>
      ))}
    </div>
  );
};

const FirstFrameLoadChart = ({ data, timeRange }) => {
  const chartData = {
    labels: data?.labels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      { label: 'iOS', data: data?.ios || [1.2, 1.15, 1.3, 1.1, 1.25, 1.18, 1.22], borderColor: '#f82581', backgroundColor: 'rgba(248,37,129,0.08)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5 },
      { label: 'Android', data: data?.android || [1.35, 1.28, 1.42, 1.32, 1.38, 1.3, 1.4], borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.08)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5 },
      { label: 'Web', data: data?.web || [1.5, 1.45, 1.6, 1.55, 1.65, 1.52, 1.58], borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.08)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5 },
    ],
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: { x: { ticks: tickStyle, grid: noGrid, border: noBorder }, y: { ticks: { ...tickStyle, callback: v => v.toFixed(1) + 's' }, grid: gridStyle, border: noBorder } },
  };
  return (
    <div className="pa-chart-card">
      <div className="pa-chart-head">
        <h6>First Frame Load Time</h6>
        <div className="pa-chart-legend-inline">
          <span><span className="pa-dot" style={{ background: '#f82581' }}></span> iOS</span>
          <span><span className="pa-dot" style={{ background: '#10b981' }}></span> Android</span>
          <span><span className="pa-dot" style={{ background: '#6366f1' }}></span> Web</span>
        </div>
      </div>
      <div className="pa-chart-body" style={{ height: 260 }}><Line data={chartData} options={options} /></div>
    </div>
  );
};

const RebufferRateChart = ({ data }) => {
  const qualities = data ? Object.keys(data) : ['360p', '480p', '720p', '1080p'];
  const values = data ? Object.values(data) : [0.3, 0.9, 1.8, 3.2];
  const chartData = {
    labels: qualities,
    datasets: [{ label: 'Rebuffer %', data: values, backgroundColor: ['rgba(16,185,129,0.6)', 'rgba(248,37,129,0.5)', 'rgba(245,158,11,0.5)', 'rgba(239,68,68,0.6)'], borderRadius: 6, borderSkipped: false, barPercentage: 0.7 }],
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.parsed.y}%` } } },
    scales: { x: { ticks: tickStyle, grid: noGrid, border: noBorder }, y: { ticks: { ...tickStyle, callback: v => v + '%' }, grid: gridStyle, border: noBorder } },
  };
  return (
    <div className="pa-chart-card">
      <div className="pa-chart-head"><h6>Rebuffer Rate by Quality</h6></div>
      <div className="pa-chart-body" style={{ height: 240 }}><Bar data={chartData} options={options} /></div>
    </div>
  );
};

const ABRSwitchingChart = ({ data }) => {
  const chartData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [{ label: 'Switches per Session', data: data || [2.1, 1.8, 1.5, 1.2, 1.3, 1.6, 2.4, 3.2, 4.1, 4.5, 4.8, 5.2, 5.5, 5.1, 4.8, 4.3, 4.1, 3.8, 3.5, 3.2, 2.9, 2.6, 2.3, 2.0], borderColor: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.08)', tension: 0.4, fill: true, pointRadius: 0, pointHoverRadius: 5, borderWidth: 2.5 }],
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: tooltipStyle },
    scales: { x: { ticks: { ...tickStyle, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 }, grid: noGrid, border: noBorder }, y: { ticks: { ...tickStyle, callback: v => v.toFixed(1) }, grid: gridStyle, border: noBorder } },
  };
  return (
    <div className="pa-chart-card">
      <div className="pa-chart-head"><h6>ABR Switching Frequency</h6><span className="pa-badge-sm">24 Hours</span></div>
      <div className="pa-chart-body" style={{ height: 240 }}><Line data={chartData} options={options} /></div>
    </div>
  );
};

const PlatformBreakdown = ({ data }) => {
  const platforms = data || [
    { platform: 'iOS', pct: 42, sessions: '2.1M', avg_watch_min: 34.5, color: '#f82581' },
    { platform: 'Android', pct: 48, sessions: '2.4M', avg_watch_min: 38.2, color: '#10b981' },
    { platform: 'Web', pct: 10, sessions: '500K', avg_watch_min: 28.1, color: '#6366f1' },
  ];
  const platformData = {
    labels: platforms.map(p => p.platform),
    datasets: [{ data: platforms.map(p => p.pct), backgroundColor: platforms.map(p => p.color || '#888'), borderWidth: 0, cutout: '68%', spacing: 3 }],
  };
  return (
    <div className="pa-chart-card">
      <div className="pa-chart-head"><h6>Platform Breakdown</h6></div>
      <div className="pa-platform-wrap">
        <div className="pa-doughnut-wrap" style={{ height: 200 }}>
          <Doughnut data={platformData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.label}: ${ctx.parsed}%` } } } }} />
        </div>
        <div className="pa-platform-legend">
          {platforms.map((p, i) => (
            <div key={i} className="pa-platform-item">
              <span className="pa-dot" style={{ background: p.color || '#888' }}></span>
              <div className="pa-platform-info">
                <strong>{p.platform}</strong>
                <span>{p.sessions} sessions</span>
                <span>{p.avg_watch_min} min avg</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PlayerSDKVersionsTable = ({ data }) => {
  const sdkVersions = data || [
    { name: 'AVPlayer', version: '2.4.1', platform: 'iOS', adoption_pct: '98.5%', crash_rate_pct: '0.04%', known_issues: 2 },
    { name: 'ExoPlayer', version: '2.19.1', platform: 'Android', adoption_pct: '97.2%', crash_rate_pct: '0.12%', known_issues: 5 },
    { name: 'HLS.js', version: '1.5.7', platform: 'Web', adoption_pct: '89.3%', crash_rate_pct: '0.08%', known_issues: 3 },
    { name: 'Shaka Player', version: '4.7.0', platform: 'Web Fallback', adoption_pct: '10.7%', crash_rate_pct: '0.15%', known_issues: 1 },
  ];
  const getCrashBadge = (rate) => {
    const v = parseFloat(rate);
    if (v < 0.05) return 'pa-badge-green';
    if (v < 0.12) return 'pa-badge-yellow';
    return 'pa-badge-orange';
  };
  return (
    <div className="pa-chart-card">
      <div className="pa-chart-head"><h6><i className="fas fa-cog"></i> Player SDK Versions</h6></div>
      <div className="pa-table-wrap">
        <table className="pa-table">
          <thead><tr><th>SDK Name</th><th>Version</th><th>Platform</th><th>Adoption %</th><th>Crash Rate</th><th>Known Issues</th></tr></thead>
          <tbody>
            {sdkVersions.map((sdk, i) => (
              <tr key={i}>
                <td><strong>{sdk.name}</strong></td>
                <td>{sdk.version}</td>
                <td>{sdk.platform}</td>
                <td><span className="pa-badge pa-badge-blue">{sdk.adoption_pct}</span></td>
                <td><span className={`pa-badge ${getCrashBadge(sdk.crash_rate_pct)}`}>{sdk.crash_rate_pct}</span></td>
                <td>{sdk.known_issues}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PlaybackErrorBreakdown = ({ data }) => {
  const defaultData = { 'Network Timeout': 32, 'Codec Unsupported': 18, 'DRM License Failure': 15, 'Buffer Underrun': 22, 'Manifest Parse Error': 8, 'Other': 5 };
  const src = data || defaultData;
  const labels = Object.keys(src);
  const values = Object.values(src);
  const chartData = {
    labels,
    datasets: [{ label: 'Errors %', data: values, backgroundColor: ['#ef4444', '#f59e0b', '#f82581', '#6366f1', '#10b981', 'rgba(255,255,255,0.1)'], borderRadius: 6, borderSkipped: false }],
  };
  const options = {
    responsive: true, maintainAspectRatio: false, indexAxis: 'y',
    plugins: { legend: { display: false }, tooltip: { ...tooltipStyle, callbacks: { label: ctx => `${ctx.parsed.x}%` } } },
    scales: { x: { ticks: { ...tickStyle, callback: v => v + '%' }, grid: gridStyle, border: noBorder }, y: { ticks: tickStyle, grid: noGrid, border: noBorder } },
  };
  return (
    <div className="pa-chart-card">
      <div className="pa-chart-head"><h6><i className="fas fa-exclamation-triangle"></i> Playback Error Breakdown</h6></div>
      <div className="pa-chart-body" style={{ height: 240 }}><Bar data={chartData} options={options} /></div>
    </div>
  );
};

const DevicePerformanceTable = ({ data }) => {
  const devices = data || [
    { name: 'iPhone 15', os: 'iOS 18.1', crash_rate_pct: '0.02%', avg_load_s: '1.1s', sessions: '542K' },
    { name: 'Samsung Galaxy S24', os: 'Android 14', crash_rate_pct: '0.08%', avg_load_s: '1.4s', sessions: '628K' },
    { name: 'Xiaomi 14', os: 'Android 14', crash_rate_pct: '0.15%', avg_load_s: '1.6s', sessions: '412K' },
    { name: 'iPad Pro', os: 'iPadOS 18.1', crash_rate_pct: '0.01%', avg_load_s: '0.95s', sessions: '285K' },
    { name: 'Chrome Desktop', os: 'Windows 11', crash_rate_pct: '0.05%', avg_load_s: '1.3s', sessions: '198K' },
    { name: 'Safari Desktop', os: 'macOS 14.3', crash_rate_pct: '0.03%', avg_load_s: '1.2s', sessions: '152K' },
  ];
  const getCrashColor = (rate) => {
    const v = parseFloat(rate);
    if (v < 0.05) return '#10b981';
    if (v < 0.10) return '#f59e0b';
    return '#ef4444';
  };
  return (
    <div className="pa-chart-card">
      <div className="pa-chart-head"><h6><i className="fas fa-mobile-alt"></i> Device Performance</h6></div>
      <div className="pa-table-wrap">
        <table className="pa-table">
          <thead><tr><th>Device</th><th>OS Version</th><th>Crash Rate</th><th>Avg Load Time</th><th>Sessions</th></tr></thead>
          <tbody>
            {devices.map((device, i) => (
              <tr key={i}>
                <td><strong>{device.name}</strong></td>
                <td>{device.os}</td>
                <td><span className="pa-badge" style={{ background: getCrashColor(device.crash_rate_pct) + '25', color: getCrashColor(device.crash_rate_pct) }}>{device.crash_rate_pct}</span></td>
                <td>{device.avg_load_s}</td>
                <td>{device.sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerAnalytics;
